import { ChromaKeyParams, DEFAULT_CHROMA_KEY_PARAMS, FRAGMENT_SHADER } from '../constants/chroma-key-shader';
import { VERTEX_SHADER } from '../constants';
import { compileShader, createProgram } from './shaders-utils';

/**
 * WebGL resources for one chroma-keyed video, with no loop of its own: whoever
 * uses it decides when to draw.
 */
export type ChromaKeyInstance = {
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  params: ChromaKeyParams;
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  texture: WebGLTexture;
  vao: WebGLVertexArrayObject;
  lastDrawnTime: number;
  uniformLocations: {
    texture: WebGLUniformLocation | null;
    keyColor: WebGLUniformLocation | null;
    similarity: WebGLUniformLocation | null;
    smoothness: WebGLUniformLocation | null;
    spill: WebGLUniformLocation | null;
  };
};

export const createChromaKeyInstance = (video: HTMLVideoElement): ChromaKeyInstance | null => {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || video.width;
  canvas.height = video.videoHeight || video.height;

  const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, alpha: true });

  if (!gl) {
    console.error('WebGL2 no disponible');
    return null;
  }

  let vertexShader: WebGLShader;
  let fragmentShader: WebGLShader;
  let program: WebGLProgram;

  try {
    vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    program = createProgram(gl, vertexShader, fragmentShader);
  } catch (error) {
    console.error('Error creando programa chroma key:', error);
    return null;
  }

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  gl.useProgram(program);
  const uniformLocations = {
    texture: gl.getUniformLocation(program, 'u_texture'),
    keyColor: gl.getUniformLocation(program, 'u_keyColor'),
    similarity: gl.getUniformLocation(program, 'u_similarity'),
    smoothness: gl.getUniformLocation(program, 'u_smoothness'),
    spill: gl.getUniformLocation(program, 'u_spill')
  };

  const texture = gl.createTexture();
  if (!texture) {
    console.error('No se pudo crear la textura');
    gl.deleteProgram(program);
    return null;
  }

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.uniform1i(uniformLocations.texture, 0);

  const vao = gl.createVertexArray();
  if (!vao) {
    console.error('No se pudo crear el VAO');
    gl.deleteTexture(texture);
    gl.deleteProgram(program);
    return null;
  }
  gl.bindVertexArray(vao);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.viewport(0, 0, canvas.width, canvas.height);

  return {
    canvas,
    video,
    gl,
    program,
    texture,
    vao,
    params: { ...DEFAULT_CHROMA_KEY_PARAMS },
    lastDrawnTime: -1,
    uniformLocations
  };
};

/**
 * Pulls the video's current frame through the shader. Returns whether it drew:
 * a paused video sitting on an already-drawn frame is skipped.
 */
export const drawChromaKey = (instance: ChromaKeyInstance): boolean => {
  const { canvas, video, gl, texture } = instance;

  const frameReady = !video.ended && video.readyState >= 2;
  const frameChanged = video.currentTime !== instance.lastDrawnTime;
  if (!frameReady || (video.paused && !frameChanged)) return false;

  if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

  const p = instance.params;
  const locs = instance.uniformLocations;
  gl.uniform3f(locs.keyColor, p.keyColor[0], p.keyColor[1], p.keyColor[2]);
  gl.uniform1f(locs.similarity, p.similarity);
  gl.uniform1f(locs.smoothness, p.smoothness);
  gl.uniform1f(locs.spill, p.spill);

  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 3);

  instance.lastDrawnTime = video.currentTime;
  return true;
};

export const destroyChromaKeyInstance = ({ gl, texture, vao, program }: ChromaKeyInstance): void => {
  gl.deleteTexture(texture);
  gl.deleteVertexArray(vao);
  gl.deleteProgram(program);
};
